/**
 * Drawer.tsx — 設計 v2 §4 **C13**（右側全高抽屜；唯一導航入口）＋ **C14**（對話清單）＋ **C15**（語言）
 *
 * 來源：`.coappery/tasks/J-ui-cosingery/design/drawer.jsx`（C13/C14）＋ `components.jsx`（C15）
 *   · **取代底部 tab bar**（設計 brief：取消底部 tab，所有導航收入右上 ⋯）—— UI v2 **步 4**
 *   · `role="dialog"` ＋ `aria-modal`；ESC 關；開時焦點入第一個掣；遮罩撳一下都關
 *   · ⚠️ 字串經 `t()`（本檔係 app 外殼，可用 i18n）
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLocale, SUPPORTED_LOCALES, type SupportedLocale } from '../i18n';
import BrandMark from './BrandMark';
import { Icons, type IconName } from './Icons';

const LOCALE_LABEL_KEYS: Record<SupportedLocale, string> = {
  'zh-HK': 'locale_zh_hk',
  en: 'locale_en',
  'zh-CN': 'locale_zh_cn',
};

export interface DrawerSession {
  id: string;
  /** 顯示名（已翻譯／已是文字） */
  name: string;
  /** 次要行（例：日期 · N 首歌） */
  meta: string;
}

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** 現時路徑（用嚟標「現行」） */
  route: string;
  onNavigate: (to: string) => void;
  sessions: DrawerSession[];
  onOpenSession: (id: string) => void;
}

export default function Drawer({ open, onClose, route, onNavigate, sessions, onOpenSession }: DrawerProps) {
  const { t, i18n } = useTranslation();
  const panel = useRef<HTMLElement>(null);
  const current = i18n.language as SupportedLocale;

  /** 開時：焦點入面板第一個掣；ESC 關（設計 C13） */
  useEffect(() => {
    if (!open) return;
    const first = panel.current?.querySelector('button');
    first?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const item = (icon: IconName, label: string, to: string | null, active = false) => {
    const I = Icons[icon];
    return (
      <button
        type="button"
        className={'nav-link' + (active ? ' nav-link--active' : '')}
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          if (to) onNavigate(to);
          onClose();
        }}
      >
        <I size="1.375rem" />
        <span>{label}</span>
      </button>
    );
  };

  const is = (to: string) => route === to || route.startsWith(to + '/');

  return (
    <div className={'drawer' + (open ? ' drawer--open' : '')}>
      <button type="button" className="drawer__scrim" tabIndex={-1} aria-hidden="true" onClick={onClose} />
      <aside ref={panel} className="drawer__panel" role="dialog" aria-modal="true" aria-label={t('drawer_aria')}>
        <div className="drawer__head">
          <BrandMark size={24} />
          <div className="topbar__spacer" />
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('sheet_close_aria')}>
            <Icons.Close />
          </button>
        </div>
        <nav className="drawer__body">
          <div className="drawer__section">
            <button
              type="button"
              className="nav-link nav-link--new"
              onClick={() => {
                onNavigate('new');
                onClose();
              }}
            >
              <Icons.NewChat size="1.375rem" />
              <span>{t('new_chat_button')}</span>
            </button>
          </div>

          {sessions.length ? (
            <div className="drawer__section">
              <h2 className="section-title">{t('history_title')}</h2>
              {sessions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="history-item"
                  onClick={() => {
                    onOpenSession(s.id);
                    onClose();
                  }}
                >
                  <span className="history-item__name">{s.name}</span>
                  <span className="history-item__meta">{s.meta}</span>
                </button>
              ))}
            </div>
          ) : null}

          <div className="drawer__section">
            {item('Diary', t('nav_songs'), '/songs', is('/songs'))}
            {item('Playlist', t('nav_playlists'), '/player', is('/player'))}
          </div>

          <div className="drawer__section">
            {item('Account', t('tab_me'), '/me', is('/me'))}
            {item('Settings', t('nav_settings'), '/settings', is('/settings'))}
          </div>
        </nav>
        <div className="drawer__foot">
          <h2 className="section-title">{t('language_label')}</h2>
          <div className="locales" role="group">
            {SUPPORTED_LOCALES.map((code) => (
              <button
                key={code}
                type="button"
                lang={code}
                className={'lang-btn' + (current === code ? ' lang-btn--active' : '')}
                aria-pressed={current === code}
                onClick={() => changeLocale(code)}
              >
                {t(LOCALE_LABEL_KEYS[code])}
              </button>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}
